<script setup>
import {computed, onMounted, ref} from "vue";
import RegistrationError from "../../../views/login/registration/components/RegistrationError.vue";
import {useStore} from "vuex";
import axios from "axios";
import CardElement from "../billing/partials/CardElement.vue";

const defaultSubmitButtonMessage = `Get Started - Risk Free<br><small>Love it or get a full refund</small>`;

const submitButtonDisabled = ref(false);
const submitButtonDisabledPrice = ref(true);
const buttonMessage = ref(defaultSubmitButtonMessage);
const stripeCardErrorMessage = ref();
const store = useStore();
const registrationComplete = ref(false);

const stripe = computed(() => {
  return store.getters['billing/stripe'];
});

const card = computed(() => {
  return store.getters['billing/newCard'];
});

const price = computed(() => {
  return store.getters['registration/price'];
});

const finalPrice = computed(() => {
  return price.value.fullPrice;
});

onMounted(async () => {
  await store.dispatch('registration/getAgencyPrice');

  submitButtonDisabledPrice.value = false;
  window.dataLayer = window.dataLayer || [];

  await store.dispatch('billing/setupStripe', {});
});

const submitStripeForm = async () => {
  stripeCardErrorMessage.value = null;
  submitButtonDisabled.value = true;
  buttonMessage.value = `Submitting payment...<br><small>This will only take a few moments!</small>`;

  const { data } = await createStripeCustomerAndIncompleteSubscription();

  const { registrationId, clientSecret } = data;

  await store.dispatch('registration/setRegistrationId', {
    registrationId
  });

  stripe.value.confirmCardPayment(clientSecret, {
    payment_method: {
      card: card.value,
    },
  })
    .then((result) => {
      if (result.error) {
        const {message} = result.error;
        submitButtonDisabled.value = false;
        stripeCardErrorMessage.value = message;
        buttonMessage.value = defaultSubmitButtonMessage;

        removeCustomer()
          .finally(() => {
            throw result.error;
          });

      } else {
        buttonMessage.value = `Success!<br><small>We're getting things ready for you!</small>`;
        completeRegistration();
      }
    })
    .catch(() => {
      submitButtonDisabled.value = false;
      stripeCardErrorMessage.value = 'Sorry, there was a problem taking payment. You have not been charged.';
    });
}

const completeRegistration = async () => {
  const {registrationId} = store.getters['registration/registrationId'];

  const { data } = await axios.post('/ajax/complete-registration', {
    registrationId: registrationId,
  });

  registrationComplete.value = true;

  window.dataLayer.push({
    'event': 'registerAgency',
  });
  window.location.href = data.url;
}

const removeCustomer = () => {
  const { registrationId } = store.getters['registration/registrationId'];

  return axios.post('/ajax/remove-customer', {registrationId});
}

const createStripeCustomerAndIncompleteSubscription = async () => {
  const { billingPeriod, currency } = store.getters['registration/configuration'];

  return axios.post('/ajax/create-agency-customer', {
    name: store.getters['registration/name'],
    email: store.getters['registration/email'],
    password: store.getters['registration/password'],
    operationCountry: store.getters['registration/operationCountry'],
    currency: currency,
    billingPeriod: billingPeriod,
  });
}

window.addEventListener('beforeunload', function () {
  if (!registrationComplete.value) {
    removeCustomer();
  }
});
</script>

<template>
  <div
    v-if="price"
    class="stripe-form"
  >
    <RegistrationError v-if="stripeCardErrorMessage">
      <span v-html="stripeCardErrorMessage" />
    </RegistrationError>

    <div class="price-header">
      <div
        v-if="!submitButtonDisabledPrice"
        class="price-container"
      >
        <div class="price">
          <span
            class="price-hero"
            v-html="finalPrice.exVatDisplay"
          />
        </div>
        <div
          v-if="price.showTax"
          class="price-tax"
          v-html="finalPrice.incVatDisplay"
        />
      </div>
      <div v-else>
        Checking best price...
      </div>
    </div>

    <div class="update-text">
      Create your account and grow your SEO revenue. Cancel at any time.
    </div>

    <CardElement
      v-if="stripe"
      :show-notice="false"
    />

    <div class="form-input-group">
      <button
        :disabled="submitButtonDisabled"
        class="btn-register"
        type="button"
        @click="submitStripeForm"
      >
        <span v-html="buttonMessage" />
      </button>
    </div>

    <div class="small-text">
      You will be charged today, but if Hike isn’t for you please email support@hikeseo.co within the first 14 days for
      a full refund.
    </div>
  </div>
</template>

<style lang="scss" scoped>
.stripe-form {
  font-family: 'DM Sans', sans-serif;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 1rem;
  background: #FFFFFF;
  border: 1px solid #D8D8D8;
  box-sizing: border-box;
  border-radius: 4px;
  gap: 1.5rem;
}

.btn-register {
  background-color: #4CE0B3;
  color: white;
  text-align: center;
  border: none;
  cursor: pointer;
  width: 100%;
  transition: background-color 0.2s ease;
  font-size: inherit;
  font-family: 'DM Sans', sans-serif;
  padding: 0.5rem;
  border-radius: 4px;

  small {
    font-size: x-small;
  }

  &:hover {
    background-color: #3BB390;
  }

  &:disabled {
    opacity: 0.7;
  }
}

.price-header {
  display: flex;
  width: 100%;
  justify-content: space-between;

  .price-container {
    display: flex;
    flex-direction: column;
    width: 100%;

    .price {
      display: flex;
      width: 100%;

      .price-hero {
        font-family: 'DM Sans', sans-serif;
        font-weight: 700;
        color: #3E3E44;
        overflow: hidden;
        line-height: 1.1;
        font-size: 3rem;
        display: flex;
        flex-direction: column;
        justify-content: space-around;
      }

      .price-details {
        display: flex;
        flex-direction: column;
        padding-top: 0.4rem;

        .no-discount {
          padding-top: 1.25rem;
        }
      }

      .price-saving {
        font-weight: 700;
        text-decoration: line-through;
        padding-left: 0.7rem;
      }
    }
  }

  .update-text {
    color: #3E3E44;
    font-family: DM Sans, sans-serif;
    font-style: normal;
    font-weight: bold;
    font-size: 12px;
    line-height: 16px;
    text-transform: uppercase;
    text-align: center;
  }

  .small-text {
    color: #3E3E44;
    font-family: DM Sans, sans-serif;
    font-style: normal;
    font-weight: normal;
    font-size: 12px;
    line-height: 16px;
    text-align: center;
  }

  .form-input-group > * {
    width: 100%;
    padding: 1rem;
    background: #FFFFFF;
    border: 1px solid #D8D8D8;
    box-sizing: border-box;
    border-radius: 4px;
  }
}

.price-switch-container {
  width: 40%;
  display: flex;
  justify-content: center;
  gap: 0.5rem;
  font-size: 0.8rem;

  .switch {
    position: relative;
    display: inline-block;
    width: 45px;
    min-width: 45px;
    height: 18px;

    input {
      opacity: 0;
      width: 0;
      height: 0;
    }
  }

  input:checked + .slider {
    background-color: #f3625a;
  }

  input:focus + .slider {
    box-shadow: 0 0 1px #f3625a;
  }

  input:checked + .slider:before {
    transform: translateX(23px);
  }

  .slider {
    position: absolute;
    cursor: pointer;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: #f3625a;
    -webkit-transition: .4s;
    transition: .4s;

    &.round {
      border-radius: 34px;

      &::before {
        position: relative;
        display: block;
        background-color: #fff;
        bottom: 4px;
        content: "";
        height: 14px;
        width: 14px;
        top: 2px;
        left: 4px;
        border-radius: 50%;
        transition: transform 0.2s ease;
      }
    }
  }

  &:before {
    position: absolute;
    content: "";
    height: 26px;
    width: 26px;
    left: 4px;
    bottom: 4px;
    background-color: white;
    -webkit-transition: .4s;
    transition: .4s;
  }
}

@media only screen and (max-width: 599px) {
  .price-header {
    flex-direction: column;
    align-items: center;

    .price-container {
      width: auto;
    }

    .price-switch-container {
      padding-top: .5rem;
    }
  }
}

.card-container {
  border: 2px solid #e3e2e2;
  padding: 18px 10px;
}

</style>
